import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  Loader2,
  Smartphone,
  X,
  Copy,
  CheckCircle2,
  AlertCircle,
  StopCircle,
  Minus,
  Maximize2,
} from 'lucide-react';
import { aiGatewayService } from '@/services/aiGateway';
import { scribePairingApi } from '@/services/api/scribePairingApi';
import { usePhoneScribeStore } from '@/stores/usePhoneScribeStore';
import { Button } from '@/components/ui/button';

type Phase =
  | 'issuing'
  | 'waiting'
  | 'connected'
  | 'reconnecting'
  | 'finalizing'
  | 'done'
  | 'superseded'
  | 'error';

interface TranscriptLine {
  id: string;
  speaker?: string;
  text: string;
}

/**
 * Phone-as-mic surface. State (open / minimized / patient context) is owned
 * by `usePhoneScribeStore` so the modal survives route changes — clinicians
 * can minimize to a corner pill, navigate the EMR, and pop back to the
 * full transcript view at any time. Mounted once at app shell level
 * (MainLayout); not used as a child component anywhere.
 */
export const RecordOnPhoneModal: React.FC = () => {
  const active = usePhoneScribeStore((s) => s.active);
  const minimized = usePhoneScribeStore((s) => s.minimized);
  const patientId = usePhoneScribeStore((s) => s.patientId);
  const recordingSessionId = usePhoneScribeStore((s) => s.recordingSessionId);
  const minimize = usePhoneScribeStore((s) => s.minimize);
  const restore = usePhoneScribeStore((s) => s.restore);
  const closeStore = usePhoneScribeStore((s) => s.close);

  const [phase, setPhase] = useState<Phase>('issuing');
  const [code, setCode] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [pairLink, setPairLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  // True once the phone has tapped Start. Decouples "is the phone
  // capturing audio right now" from "how many chunks has the server
  // seen yet" — the latter lags by ~30s due to the rolling MediaRecorder
  // cycle. Drives the Stop-enabled guard and the timer.
  const [phoneIsRecording, setPhoneIsRecording] = useState(false);
  // Set by the phone when the clinician taps Pause / Resume on the
  // mobile recorder. Independent of `phoneIsRecording` so we keep the
  // session-active surface alive while toggling pause state.
  const [phonePaused, setPhonePaused] = useState(false);
  // Provenance of the paired phone: the network it connected from + the
  // user-agent it announced. Surfaced so the doctor can spot a wrong-room
  // scan ("phone connected from 8.8.8.8 — that's not me").
  const [pairProvenance, setPairProvenance] = useState<{
    remoteIp?: string;
    userAgent?: string;
  } | null>(null);
  // Number of audio chunks the backend has acknowledged from the phone.
  // Kept separately for the "have we got audio in the buffer yet"
  // distinction, used by the minimum-audio guard.
  const [chunkCount, setChunkCount] = useState(0);
  // Wall-clock seconds since phone tapped Start, used for the pill's
  // running timer.
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const sessionIdRef = useRef<string>('');
  const issuedRef = useRef(false);
  const finishedRef = useRef(false);
  const phaseRef = useRef<Phase>('issuing');
  phaseRef.current = phase;
  const recordingStartedAtRef = useRef<number | null>(null);

  // ---------------------------------------------------------------------------
  // Issue pairing code on open
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!active) {
      issuedRef.current = false;
      finishedRef.current = false;
      recordingStartedAtRef.current = null;
      setTranscript([]);
      setChunkCount(0);
      setPhoneIsRecording(false);
      setRecordingSeconds(0);
      setPairProvenance(null);
      return;
    }
    if (issuedRef.current) return;
    issuedRef.current = true;
    finishedRef.current = false;

    setPhase('issuing');
    setError(null);
    setCode(null);
    setQrDataUrl(null);
    setTranscript([]);
    setChunkCount(0);
    setPhoneIsRecording(false);
    setRecordingSeconds(0);

    (async () => {
      try {
        if (!aiGatewayService.isConnected()) {
          await aiGatewayService.connect();
        }
        const desktopClientId = aiGatewayService.getSessionId();
        if (!desktopClientId) {
          throw new Error('Voice service session not ready yet — try again in a moment.');
        }

        sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;

        aiGatewayService.scribeStartSession({
          sessionId: sessionIdRef.current,
          patientId,
          recordingSessionId,
          language: 'auto',
          soapSection: 'full',
        });

        const issued = await scribePairingApi.issue({
          desktopClientId,
          sessionId: sessionIdRef.current,
          patientId,
          recordingSessionId,
        });

        const origin = window.location.origin;
        const link = `${origin}/m/scribe/${encodeURIComponent(issued.code)}`;
        const dataUrl = await QRCode.toDataURL(link, { width: 256, margin: 1 });

        setCode(issued.code);
        setPairLink(link);
        setQrDataUrl(dataUrl);
        setPhase('waiting');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start phone pairing';
        setError(msg);
        setPhase('error');
      }
    })();
  }, [active, patientId, recordingSessionId]);

  // ---------------------------------------------------------------------------
  // Live transcript / pairing / soap event subscriptions
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!active) return;

    const offPaired = aiGatewayService.on(
      'scribe.phone-paired',
      (data: { sessionId?: string; remoteIp?: string; userAgent?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhase('connected');
        if (data?.remoteIp || data?.userAgent) {
          setPairProvenance({ remoteIp: data.remoteIp, userAgent: data.userAgent });
        }
      },
    );
    const offDisconnected = aiGatewayService.on(
      'scribe.phone-disconnected',
      (data: { sessionId?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhase('reconnecting');
      },
    );
    const offChunk = aiGatewayService.on(
      'scribe.chunk-received',
      (data: { sessionId?: string; chunksReceived?: number }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        // Fallback timer anchor — only used if the phone-recording-started
        // event got dropped or arrived after the first chunk somehow.
        if (recordingStartedAtRef.current === null) {
          recordingStartedAtRef.current = Date.now();
        }
        setChunkCount(typeof data?.chunksReceived === 'number' ? data.chunksReceived : (c) => c + 1);
      },
    );
    // Authoritative timer anchor: phone tapped Start. Lets the desktop
    // timer match the phone's local timer instead of lagging by the
    // first-chunk-rolling-cycle delay (~30s).
    const offRecordingStarted = aiGatewayService.on(
      'scribe.phone-recording-started',
      (data: { sessionId?: string; startedAt?: number }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        const ts = typeof data?.startedAt === 'number' ? data.startedAt : Date.now();
        recordingStartedAtRef.current = ts;
        setPhoneIsRecording(true);
      },
    );
    // Phone tapped Pause — flip the local UI to a paused state. Doesn't
    // alter `phoneIsRecording` so the session-active surface (Stop
    // button, timer pinned at the pause moment) stays mounted.
    const offRecordingPaused = aiGatewayService.on(
      'scribe.phone-recording-paused',
      (data: { sessionId?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhonePaused(true);
      },
    );
    const offRecordingResumed = aiGatewayService.on(
      'scribe.phone-recording-resumed',
      (data: { sessionId?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhonePaused(false);
      },
    );
    // Phone-side stop or backend ended the session for any reason.
    const offSessionEnded = aiGatewayService.on(
      'scribe.session-ended',
      (data: { sessionId?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhoneIsRecording(false);
        setPhonePaused(false);
      },
    );
    const offPartial = aiGatewayService.on(
      'scribe.transcription-partial',
      (data: {
        text?: string;
        speaker?: string;
        chunkIndex?: number;
        sessionId?: string;
        segments?: Array<{ text: string; speaker?: string }>;
      }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setTranscript((prev) => {
          const next = [...prev];
          if (Array.isArray(data?.segments) && data.segments.length > 0) {
            data.segments.forEach((s, i) => {
              const text = (s?.text ?? '').trim();
              if (!text) return;
              next.push({
                id: `${data.chunkIndex ?? Date.now()}-${i}`,
                speaker: s.speaker,
                text,
              });
            });
          } else {
            const text = (data?.text ?? '').trim();
            if (text) {
              next.push({
                id: `${data.chunkIndex ?? Date.now()}`,
                speaker: data.speaker,
                text,
              });
            }
          }
          return next;
        });
      },
    );
    const offResult = aiGatewayService.on(
      'scribe.soap-result',
      (data: { sessionId?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        setPhase('done');
      },
    );
    const offError = aiGatewayService.on(
      'scribe.error',
      (data: { message?: string; sessionId?: string; code?: string }) => {
        if (data?.sessionId && data.sessionId !== sessionIdRef.current) return;
        const msg = data?.message ?? 'Recording error';
        setError(msg);
        if (
          phaseRef.current === 'finalizing' ||
          phaseRef.current === 'connected' ||
          phaseRef.current === 'reconnecting' ||
          phaseRef.current === 'waiting'
        ) {
          finishedRef.current = true;
          setPhase('error');
        }
      },
    );
    const offSuperseded = aiGatewayService.on('scribe.session-superseded', () => {
      finishedRef.current = true;
      setPhase('superseded');
    });

    return () => {
      offPaired();
      offDisconnected();
      offChunk();
      offRecordingStarted();
      offRecordingPaused();
      offRecordingResumed();
      offSessionEnded();
      offPartial();
      offResult();
      offError();
      offSuperseded();
    };
  }, [active]);

  // Drive the timer purely from elapsed wall-clock so a re-render at any
  // pace stays honest (vs. counting setInterval ticks that can drift).
  useEffect(() => {
    // While paused, freeze the timer at its last value — the elapsed
    // wall-clock continues to advance but the on-mic clock should not.
    if (!active || !phoneIsRecording || phonePaused) return;
    const t = setInterval(() => {
      const startedAt = recordingStartedAtRef.current;
      if (startedAt === null) return;
      setRecordingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [active, phoneIsRecording, phonePaused]);

  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    transcriptScrollRef.current?.scrollTo({
      top: transcriptScrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [transcript]);

  const handleCopyLink = async () => {
    if (!pairLink) return;
    try {
      await navigator.clipboard.writeText(pairLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be denied — ignore */
    }
  };

  const handleStop = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setPhase('finalizing');
    aiGatewayService.scribeEndRecording({
      type: 'end-recording',
      sessionId: sessionIdRef.current,
      patientId,
    });
  };

  // Pairing codes are single-use. If the phone tab closed before
  // reconnecting, the original code is gone and the phone has nothing
  // to redeem with. Re-issue a fresh code under the SAME backend
  // sessionId so any audio already buffered server-side stays attached
  // to this session and the doctor can keep going.
  const [regenerating, setRegenerating] = useState(false);
  const handleRegenerateCode = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(null);
    try {
      const desktopClientId = aiGatewayService.getSessionId();
      if (!desktopClientId) {
        throw new Error('Voice service session not ready yet — try again in a moment.');
      }
      const issued = await scribePairingApi.issue({
        desktopClientId,
        sessionId: sessionIdRef.current,
        patientId,
        recordingSessionId,
      });
      const origin = window.location.origin;
      const link = `${origin}/m/scribe/${encodeURIComponent(issued.code)}`;
      const dataUrl = await QRCode.toDataURL(link, { width: 256, margin: 1 });
      setCode(issued.code);
      setPairLink(link);
      setQrDataUrl(dataUrl);
      setPhoneIsRecording(false);
      setPairProvenance(null);
      recordingStartedAtRef.current = null;
      setPhase('waiting');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to issue a new pairing code';
      setError(msg);
    } finally {
      setRegenerating(false);
    }
  };

  // "That's not my phone" escape hatch. Disconnects whatever phone is
  // currently paired and flips the modal back to the QR so the doctor
  // can re-issue or back out. The pairing code is single-use, so the
  // kicked phone can't reconnect with the same one — they'd need a
  // fresh code.
  const handleCancelPairing = () => {
    aiGatewayService.scribeKickPhone();
    setPhoneIsRecording(false);
    setChunkCount(0);
    setPairProvenance(null);
    recordingStartedAtRef.current = null;
    // Drop back to "waiting" so the doctor sees the QR again. The
    // backend session is still alive on the desktop side; the doctor
    // can issue a fresh code from here OR close the modal entirely.
    if (phaseRef.current === 'connected' || phaseRef.current === 'reconnecting') {
      setPhase('waiting');
    }
  };

  const handleClose = () => {
    const p = phaseRef.current;
    const ownsLiveSession =
      p === 'waiting' ||
      p === 'connected' ||
      p === 'reconnecting' ||
      p === 'finalizing';
    if (ownsLiveSession && !finishedRef.current) {
      finishedRef.current = true;
      try {
        aiGatewayService.scribeEndRecording({
          type: 'end-recording',
          sessionId: sessionIdRef.current,
          patientId,
        });
      } catch {
        /* socket may already be down — best-effort cleanup */
      }
    }
    closeStore();
  };

  if (!active) return null;

  // ---------------------------------------------------------------------------
  // Minimized pill
  // ---------------------------------------------------------------------------
  const formatTimer = (s: number) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (minimized) {
    const isLive = phase === 'connected' || phase === 'reconnecting';
    // "Recording" copy = phone is actively capturing (synced timer).
    // Stop-enabled = also need at least one chunk in the buffer so we
    // don't fire a synth on empty audio.
    const recording = phoneIsRecording;
    const stopEnabled = phoneIsRecording && chunkCount > 0;
    return (
      <div
        className="fixed z-40 bottom-4 right-4 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
          color: 'var(--color-text-primary)',
        }}
        role="status"
      >
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{
            backgroundColor:
              phase === 'reconnecting'
                ? 'var(--color-warning, #f59e0b)'
                : phase === 'error' || phase === 'superseded'
                ? 'var(--color-error, #dc2626)'
                : recording
                ? 'var(--color-error, #dc2626)'
                : 'var(--color-success, #16a34a)',
            animation: recording && phase !== 'reconnecting' ? 'pulse 1.5s infinite' : undefined,
          }}
        />
        <Smartphone className="w-4 h-4" style={{ color: 'var(--color-primary-600)' }} />
        <span className="text-xs font-medium">
          {phase === 'waiting'
            ? 'Waiting for phone…'
            : phase === 'reconnecting'
            ? 'Phone disconnected — open to re-pair'
            : recording
            ? `Recording · ${formatTimer(recordingSeconds)}`
            : isLive
            ? 'Phone ready — tap Start on phone'
            : phase === 'finalizing'
            ? 'Finalizing…'
            : phase === 'done'
            ? 'Note generated'
            : phase === 'superseded'
            ? 'Session ended'
            : phase === 'error'
            ? 'Phone error'
            : 'Phone scribe'}
        </span>
        {isLive && stopEnabled && (
          <button
            type="button"
            onClick={handleStop}
            className="ms-1 inline-flex items-center justify-center w-6 h-6 rounded-full text-white"
            style={{ backgroundColor: 'var(--color-error, #dc2626)' }}
            aria-label="Stop recording"
            title="Stop recording"
          >
            <StopCircle className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={restore}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-black/5"
          aria-label="Open phone scribe"
          title="Open"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-black/5"
          aria-label="Cancel phone session"
          title="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Full modal
  // ---------------------------------------------------------------------------
  const isLive = phase === 'connected' || phase === 'reconnecting';
  // Same split as the pill: copy/timer track the phone's actual state;
  // Stop only enables once we have audio in the buffer.
  const recording = phoneIsRecording;
  const stopEnabled = phoneIsRecording && chunkCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[90vh]"
        style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
            <h2 className="text-base font-semibold">Record from phone</h2>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimize is only useful while there's a live session — hide
                it on terminal phases so we don't tease a dead pill. */}
            {(isLive || phase === 'waiting' || phase === 'finalizing') && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={minimize}
                aria-label="Minimize"
                title="Minimize — keep recording while you navigate"
              >
                <Minus />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClose}
              aria-label="Close"
            >
              <X />
            </Button>
          </div>
        </div>

        <div className="px-5 py-5 overflow-y-auto flex-1">
          {phase === 'issuing' && (
            <div
              className="flex items-center gap-3 py-8 justify-center text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating pairing code…
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col gap-3">
              <div
                className="flex items-start gap-2 p-3 rounded-md text-sm"
                style={{
                  backgroundColor: 'var(--color-error-bg, #fee2e2)',
                  color: 'var(--color-error, #b91c1c)',
                }}
              >
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{error ?? 'Something went wrong.'}</span>
              </div>
              <Button
                type="button"
                size="xs"
                onClick={handleClose}
                className="self-end"
              >
                Close
              </Button>
            </div>
          )}

          {phase === 'waiting' && qrDataUrl && code && (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Scan this QR with your phone camera, or open the link below.
                Live transcript will appear here while you record.
              </p>
              <div className="flex justify-center">
                <img
                  src={qrDataUrl}
                  alt="Pairing QR code"
                  className="rounded-md border"
                  style={{ borderColor: 'var(--color-border-primary)' }}
                />
              </div>
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md"
                style={{ backgroundColor: 'var(--color-bg-secondary)' }}
              >
                <code
                  className="font-mono text-sm tracking-wider"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {code}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleCopyLink}
                >
                  {copied ? <CheckCircle2 /> : <Copy />}
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
              <div
                className="flex items-center gap-2 text-xs"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Waiting for phone to connect…
              </div>
            </div>
          )}

          {isLive && (
            <div className="space-y-4">
              <div
                className="flex items-center gap-3 px-3 py-2 rounded-md"
                style={{
                  backgroundColor:
                    phase === 'reconnecting'
                      ? 'var(--color-warning-bg, #fef3c7)'
                      : recording
                      ? 'var(--color-success-bg, #dcfce7)'
                      : 'var(--color-bg-secondary)',
                  color:
                    phase === 'reconnecting'
                      ? 'var(--color-warning-text, #92400e)'
                      : recording
                      ? 'var(--color-success, #166534)'
                      : 'var(--color-text-secondary)',
                }}
              >
                {phase === 'reconnecting' ? (
                  <div className="flex items-center justify-between gap-3 w-full">
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span className="text-sm">
                        Phone disconnected. The pairing code is single-use —
                        generate a new one to reconnect.
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="xs"
                      onClick={handleRegenerateCode}
                      loading={regenerating}
                      className="shrink-0"
                    >
                      {regenerating ? 'Generating…' : 'Generate new code'}
                    </Button>
                  </div>
                ) : recording ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-sm font-medium">
                      Recording from phone · {formatTimer(recordingSeconds)}
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">
                      Phone connected — tap <strong>Start</strong> on the phone to begin recording
                    </span>
                  </>
                )}
              </div>

              {/* Provenance + cancel-pairing escape hatch. Lets the doctor
                  reject a pairing they don't recognise (e.g. someone in
                  the room scanned the QR before they could). The
                  pairing code is single-use, so a kicked phone can't
                  reconnect — the doctor would issue a fresh code. */}
              {pairProvenance && (
                <div
                  className="flex items-start justify-between gap-2 px-3 py-2 rounded-md text-xs"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      Paired phone
                    </span>
                    {pairProvenance.remoteIp && (
                      <span className="font-mono">From {pairProvenance.remoteIp}</span>
                    )}
                    {pairProvenance.userAgent && (
                      <span
                        className="truncate"
                        title={pairProvenance.userAgent}
                        style={{ color: 'var(--color-text-tertiary)' }}
                      >
                        {pairProvenance.userAgent}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelPairing}
                    className="shrink-0 text-xs px-2 py-1 rounded hover:bg-black/5"
                    style={{ color: 'var(--color-error, #b91c1c)' }}
                    title="That's not my phone — disconnect it"
                  >
                    Not me
                  </button>
                </div>
              )}

              <div
                ref={transcriptScrollRef}
                className="rounded-md border p-3 text-sm h-56 overflow-y-auto"
                style={{
                  borderColor: 'var(--color-border-primary)',
                  backgroundColor: 'var(--color-bg-secondary)',
                }}
              >
                {transcript.length === 0 ? (
                  <div
                    className="flex items-center justify-center h-full text-xs italic"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {recording ? 'Listening…' : 'Transcript will appear once recording starts.'}
                  </div>
                ) : (
                  transcript.map((line) => (
                    <div key={line.id} className="mb-1.5 leading-relaxed">
                      {line.speaker && (
                        <span
                          className="font-semibold me-1.5 text-xs uppercase tracking-wide"
                          style={{ color: 'var(--color-primary-600)' }}
                        >
                          {line.speaker}:
                        </span>
                      )}
                      <span style={{ color: 'var(--color-text-primary)' }}>{line.text}</span>
                    </div>
                  ))
                )}
              </div>

              <Button
                type="button"
                variant="destructive"
                block
                onClick={handleStop}
                disabled={!stopEnabled}
                title={
                  !phoneIsRecording
                    ? 'Tap Start on the phone first'
                    : !stopEnabled
                    ? 'Waiting for first audio chunk to arrive…'
                    : 'Stop and generate SOAP note'
                }
              >
                <StopCircle />
                {recording && stopEnabled
                  ? 'Stop recording'
                  : recording
                  ? 'Buffering first audio…'
                  : 'Waiting for audio…'}
              </Button>
              <p className="text-xs text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                You can also tap Stop on the phone — either ends the consult.
              </p>
            </div>
          )}

          {phase === 'finalizing' && (
            <div
              className="flex items-center gap-3 py-8 justify-center text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating SOAP note…
            </div>
          )}

          {phase === 'done' && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="w-10 h-10" style={{ color: 'var(--color-success)' }} />
              <div className="text-base font-semibold">Note generated</div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                The SOAP draft is ready on this dashboard.
              </p>
              <Button
                type="button"
                onClick={handleClose}
                className="mt-2"
              >
                Close
              </Button>
            </div>
          )}

          {phase === 'superseded' && (
            <div className="flex flex-col items-start gap-2 p-3 rounded-md text-sm"
                 style={{
                   backgroundColor: 'var(--color-warning-bg, #fef3c7)',
                   color: 'var(--color-warning-text, #92400e)',
                 }}>
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="w-5 h-5" /> Session ended
              </div>
              <span>
                Another scribe session was started on this account. This one was closed
                so you don't have two consults running at the same time.
              </span>
              <Button
                type="button"
                size="xs"
                onClick={handleClose}
                className="mt-2 self-end"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordOnPhoneModal;
